import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVersionString } from '../version';

interface ClassifierStats {
  overall: {
    totalRequests: number;
    totalTokens: number;
    totalCostUsd: number;
    successfulRequests: number;
    failedRequests: number;
    avgDurationMs: number;
    firstRequest: string;
    lastRequest: string;
  };
  today: {
    todayRequests: number;
    todayTokens: number;
    todayCostUsd: number;
  };
  week: {
    weekRequests: number;
    weekTokens: number;
    weekCostUsd: number;
  };
  month: {
    monthRequests: number;
    monthTokens: number;
    monthCostUsd: number;
  };
  categoryBreakdown: Array<{
    category: string;
    count: number;
    costUsd: number;
  }>;
  platformBreakdown: Array<{
    platform: string;
    count: number;
    costUsd: number;
  }>;
  dailyUsage: Array<{
    date: string;
    requests: number;
    tokens: number;
    costUsd: number;
  }>;
}

interface ClassifierSettings {
  openaiKeySet: boolean;
  openaiKeyMasked: string | null;
  categories: Array<{
    id: string;
    name: string;
    emoji: string;
    enabled: boolean;
  }>;
  classifierRunning: boolean;
  model: string;
  cycleIntervalSeconds: number;
  batchSize: number;
}

const ClassifierSettings: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<ClassifierStats | null>(null);
  const [settings, setSettings] = useState<ClassifierSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, settingsRes] = await Promise.all([
        fetch('/api/admin/classifier/stats', { credentials: 'include' }),
        fetch('/api/admin/classifier/settings', { credentials: 'include' }),
      ]);

      if (!statsRes.ok || !settingsRes.ok) {
        throw new Error('Failed to fetch classifier data');
      }

      const statsData = await statsRes.json();
      const settingsData = await settingsRes.json();

      setStats(statsData);
      setSettings(settingsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const getCategoryEmoji = (category: string): string => {
    const emojiMap: Record<string, string> = {
      gambling_pachinko: '🎰',
      gambling_online: '🎲',
      gambling_public: '🏇',
      gambling_game: '🎮',
      gambling_gacha_premium: '🎴',
      politics: '🗳️',
      healthcare: '🏥',
      education: '📚',
      economics: '💰',
      tourism: '✈️',
      culture: '🎭',
      sports: '⚽',
      youtube: '▶️',
      other: '📱',
    };
    return emojiMap[category] || '📱';
  };

  const formatCost = (cost: number | string | null | undefined): string => {
    const numCost = Number(cost) || 0;
    if (numCost < 0.01) {
      return `$${(numCost * 1000).toFixed(3)}m`;  // Show in millicents for very small amounts
    }
    return `$${numCost.toFixed(4)}`;
  };

  const formatNumber = (num: number | string | null | undefined): string => {
    const n = Number(num) || 0;
    return new Intl.NumberFormat('en-US').format(Math.round(n));
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('classifier.title')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{getVersionString()}</p>
          </div>
        </div>
        {/* System Status */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('classifier.settings')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Classifier Status</div>
              <div className={`text-lg font-bold ${settings?.classifierRunning ? 'text-green-600' : 'text-red-600'}`}>
                {settings?.classifierRunning ? '✅ Running' : '❌ Stopped'}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">OpenAI API Key</div>
              <div className={`text-lg font-bold ${settings?.openaiKeySet ? 'text-green-600' : 'text-red-600'}`}>
                {settings?.openaiKeySet ? 'Configured' : 'Not Set'}
              </div>
              {settings?.openaiKeyMasked && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{settings.openaiKeyMasked}</div>
              )}
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('classifier.model')}</div>
              <div className="text-lg font-bold text-blue-600">{settings?.model || 'N/A'}</div>
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('classifier.cycleInterval')}</div>
              <div className="text-lg font-bold text-purple-600">{settings?.cycleIntervalSeconds || 0}s</div>
            </div>
          </div>
        </div>

        {/* Usage Statistics - Today, Week, Month */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('classifier.dailyUsage')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Today */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-800">
              <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-3">Today</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Requests:</span>
                  <span className="font-bold">{formatNumber(stats?.today?.todayRequests || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tokens:</span>
                  <span className="font-bold">{formatNumber(stats?.today?.todayTokens || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Cost:</span>
                  <span className="font-bold text-green-600">{formatCost(stats?.today?.todayCostUsd || 0)}</span>
                </div>
              </div>
            </div>

            {/* This Week */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-2 border-purple-200 dark:border-purple-800">
              <h3 className="font-bold text-purple-900 dark:text-purple-300 mb-3">This Week</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Requests:</span>
                  <span className="font-bold">{formatNumber(stats?.week?.weekRequests || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tokens:</span>
                  <span className="font-bold">{formatNumber(stats?.week?.weekTokens || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Cost:</span>
                  <span className="font-bold text-green-600">{formatCost(stats?.week?.weekCostUsd || 0)}</span>
                </div>
              </div>
            </div>

            {/* This Month */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border-2 border-green-200 dark:border-green-800">
              <h3 className="font-bold text-green-900 dark:text-green-300 mb-3">This Month</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Requests:</span>
                  <span className="font-bold">{formatNumber(stats?.month?.monthRequests || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tokens:</span>
                  <span className="font-bold">{formatNumber(stats?.month?.monthTokens || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Cost:</span>
                  <span className="font-bold text-green-600">{formatCost(stats?.month?.monthCostUsd || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall Statistics */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('classifier.totalRequests')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('classifier.totalRequests')}</div>
              <div className="text-2xl font-bold text-blue-600">{formatNumber(stats?.overall?.totalRequests || 0)}</div>
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Tokens</div>
              <div className="text-2xl font-bold text-purple-600">{formatNumber(stats?.overall?.totalTokens || 0)}</div>
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('classifier.totalCost')}</div>
              <div className="text-2xl font-bold text-green-600">{formatCost(stats?.overall?.totalCostUsd || 0)}</div>
            </div>
            <div className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Success Rate</div>
              <div className="text-2xl font-bold text-green-600">
                {stats?.overall?.totalRequests && stats.overall.successfulRequests != null
                  ? `${((stats.overall.successfulRequests / stats.overall.totalRequests) * 100).toFixed(1)}%`
                  : <span className="text-gray-400 text-lg">No data</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('classifier.categoryBreakdown')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stats?.categoryBreakdown?.map((cat) => (
              <div key={cat.category} className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg border border-gray-200 dark:border-warm-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{getCategoryEmoji(cat.category)}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {cat.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                <div className="text-lg font-bold text-blue-600">{formatNumber(cat.count)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{formatCost(cat.costUsd)}</div>
              </div>
            ))}
          </div>
          {(!stats?.categoryBreakdown || stats.categoryBreakdown.length === 0) && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">{t('common.noData')}</div>
          )}
        </div>

        {/* Platform Breakdown */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Platform Breakdown</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stats?.platformBreakdown?.map((plat) => (
              <div key={plat.platform} className="bg-gray-50 dark:bg-warm-gray-700 p-4 rounded-lg border border-gray-200 dark:border-warm-gray-600">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">{plat.platform}</div>
                <div className="text-lg font-bold text-blue-600">{formatNumber(plat.count)}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{formatCost(plat.costUsd)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Categories */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Available Categories (13 Total)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {settings?.categories?.map((cat) => (
              <div
                key={cat.id}
                className={`p-3 rounded-lg border-2 ${
                  cat.enabled ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-warm-gray-700 border-gray-200 dark:border-warm-gray-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{cat.emoji}</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{cat.name}</span>
                  </div>
                  {cat.enabled ? (
                    <span className="text-green-600 text-sm">✓ Enabled</span>
                  ) : (
                    <span className="text-gray-400 text-sm">Disabled</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Japan Market Enhancement - Updated Jan 2026 */}
        <div className="bg-gradient-to-r from-red-50 to-white dark:from-red-900/20 dark:to-warm-gray-800 border-2 border-red-200 dark:border-red-800 rounded-lg p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-3xl">🇯🇵</span>
            <div>
              <h3 className="font-bold text-red-900 dark:text-red-300 text-lg">Japan Market Enhancement</h3>
              <span className="text-xs text-gray-500">Spec v2.2 - Updated February 26, 2026</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Gambling Categories with Legal Status */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-lg p-4 border border-gray-200 dark:border-warm-gray-700">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Gambling Sub-Categories (5 Types)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span>🎰</span>
                    <span>Pachinko</span>
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">LEGAL</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span>🎲</span>
                    <span>Online Casino</span>
                  </span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">ILLEGAL</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span>🏇</span>
                    <span>Public (JRA/BOAT)</span>
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">LEGAL</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span>🎮</span>
                    <span>Game (Virtual Gacha)</span>
                  </span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">LEGAL</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span>🎴</span>
                    <span>Premium Gacha (Oripa)</span>
                  </span>
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">GRAY</span>
                </div>
              </div>
            </div>

            {/* Japan Keywords */}
            <div className="bg-white dark:bg-warm-gray-800 rounded-lg p-4 border border-gray-200 dark:border-warm-gray-700">
              <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Japan-Specific Keywords</h4>
              <div className="space-y-2 text-xs">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Pachinko:</span>
                  <span className="text-gray-500 ml-1">パチンコ, パチスロ, マルハン, ダイナム, キコーナ, ZENT</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Public:</span>
                  <span className="text-gray-500 ml-1">JRA, 競馬, BOAT RACE, 競艇, 競輪, 宝くじ, toto</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Online:</span>
                  <span className="text-gray-500 ml-1">オンラインカジノ, ネットカジノ, ブックメーカー</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Game:</span>
                  <span className="text-gray-500 ml-1">カジノゲーム, ガチャ, 課金, SSR, UR</span>
                </div>
                <div>
                  <span className="font-medium text-yellow-600">Premium:</span>
                  <span className="text-gray-500 ml-1">オリパ, DOPA, Clove, HYPES, スニーカーガチャ, PSA10</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
            <strong>Note:</strong> Online casino ads banned since Sept 2025. Premium Gacha (oripa/sneaker gacha) is in legal gray zone.
            Config: <code className="bg-gray-100 px-1 rounded">classifier_config.yaml</code> with 170+ Japan-specific terms.
          </div>
        </div>

        {/* Cost Efficiency Note */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
          <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2">Cost Efficiency</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Using GPT-4o-mini at ~$0.0001 per classification. Estimated monthly cost for 100 ads/day: ~$0.30.
            The system has 4-layer cost protection to ensure each ad is classified exactly once.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClassifierSettings;
